/**
 * Mobile Authentication Guard Component
 * Protects routes and redirects unauthenticated users
 */

import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spin, Typography } from 'antd';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import { 
  selectIsAuthenticated, 
  selectIsInitialized, 
  selectIsLoading,
  selectIsRider,
  ensureValidTokens 
} from '../../store/slices/authSlice';

const { Text } = Typography;

interface AuthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  requireAuth?: boolean;
}

const AuthGuard: FC<AuthGuardProps> = ({ 
  children, 
  fallback,
  requireAuth = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const isLoading = useAppSelector(selectIsLoading);
  const isRider = useAppSelector(selectIsRider); // This will check for 'individual' type

  // Initialize authentication
  useEffect(() => {
    console.log('🔐 AuthGuard: Initialization check', {
      mockAuthEnabled: import.meta.env.VITE_USE_MOCK_AUTH,
      isInitialized,
      isAuthenticated,
      hasLoggedOut: sessionStorage.getItem('mock_logged_out')
    });
    
    // Check if we should use mock auth
    if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
      // Check if user has explicitly logged out
      const hasLoggedOut = sessionStorage.getItem('mock_logged_out');
      if (hasLoggedOut === 'true') {
        console.log('🔐 AuthGuard: User has logged out, not auto-logging in');
        // User logged out, don't auto-login
        if (isAuthenticated) {
          // Clear authentication state
          dispatch({
            type: 'auth/initialize/fulfilled',
            payload: {
              user: null,
              isAuthenticated: false,
              isOffline: false,
              biometricEnabled: false,
              rememberDevice: false
            }
          });
        }
      } else if (!isInitialized) {
        console.log('🔐 AuthGuard: Using mock authentication for development');
        // Simulate successful auth initialization
        const mockUser = {
          sub: 'mock_rider_123',
          email: 'testrider@example.com',
          email_verified: true,
          'custom:user_type': 'individual' as const,
          'cognito:groups': [],
          given_name: 'Test',
          family_name: 'Rider',
          phone_number: '+9771234567890',
          'custom:full_name': 'Test Rider User'
        };
        console.log('🔐 AuthGuard: Dispatching mock user:', mockUser);
        dispatch({
          type: 'auth/initialize/fulfilled',
          payload: {
            user: mockUser,
            isAuthenticated: true,
            isOffline: false,
            biometricEnabled: false,
            rememberDevice: false
          }
        });
      }
    } else if (isInitialized && isAuthenticated) {
      console.log('🔐 AuthGuard: Ensuring valid tokens');
      dispatch(ensureValidTokens());
    }
  }, [isInitialized, isAuthenticated, dispatch]);

  // Redirect logic
  useEffect(() => {
    if (!isInitialized) return; // Wait for initialization

    if (requireAuth && !isAuthenticated) {
      // Save the attempted location for redirect after login
      const from = location.pathname + location.search;
      navigate('/login', { 
        replace: true,
        state: { from: from !== '/login' ? from : '/dashboard' }
      });
    } else if (requireAuth && isAuthenticated && !isRider) {
      // Authenticated but not a rider - show unauthorized
      navigate('/unauthorized', { replace: true });
    } else if (!requireAuth && isAuthenticated) {
      // For public routes like login, redirect authenticated users
      const from = location.state?.from || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isInitialized, requireAuth, isRider, navigate, location]);

  // Show loading state while initializing
  if (!isInitialized || isLoading) {
    return fallback || (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '16px',
          background: '#f0f2f5',
        }}
      >
        <Spin size="large" />
        <Text type="secondary">Loading...</Text>
      </div>
    );
  }

  // Show children if auth requirements are met
  if (requireAuth && isAuthenticated && isRider) {
    return <>{children}</>;
  }

  if (!requireAuth && !isAuthenticated) {
    return <>{children}</>;
  }

  // Default fallback
  return fallback || null;
};

export default AuthGuard;